import { useMemo } from 'react';
import { useMetricFormatter } from 'src/utils/Hooks';
import { calculatePercentChange, getSearchAppName } from 'src/utils/app';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import { FIELD_PATH_SEPARATOR } from 'src/utils/entityDefinitions/entityDefinitionTypes';
import { ValueOf } from 'sl-api-connector';
import { useMetricByWeekIdV2, UseMetricByWeekIdV2Args } from './useMetricByWeekIdV2';
import { SplineChartV2DataPoint, SplineChartV2Series } from './SplineChartV2';

/**
 * Arguments for useDerivedMetricByWeekIdV2 hook
 */
export interface UseDerivedMetricByWeekIdV2Args {
  /** The index name (e.g., 'sales') */
  indexName: string;
  /** The field name for the derived metric (e.g., 'purchaseRate') */
  fieldName: string;
  /** Entity type for field lookup */
  entityType?: string;
  /** Additional args passed to useMetricByWeekIdV2 */
  useMetricByWeekIdV2Args?: Partial<UseMetricByWeekIdV2Args>;
}

/**
 * Return type for the useDerivedMetricByWeekIdV2 hook
 */
export interface UseDerivedMetricByWeekIdV2Result {
  // ============ Series Data (ready for SplineChartV2) ============
  /** Primary series data ready for SplineChartV2 */
  primarySeries: SplineChartV2Series;
  /** Comparison series data ready for SplineChartV2 */
  comparisonSeries: SplineChartV2Series;

  // ============ Raw Data Points ============
  /** Raw primary data points */
  primaryData: SplineChartV2DataPoint[];
  /** Raw comparison data points */
  comparisonData: SplineChartV2DataPoint[];

  // ============ Loading State ============
  /** Whether the data is loading */
  isLoading: boolean;

  // ============ Field Info ============
  /** The metric type for this field */
  metricType: ValueOf<typeof METRICTYPE>;
  /** The field configuration from INDEX_FIELDS */
  field: ReturnType<typeof INDEX_FIELDS.getField>;

  // ============ Raw Numeric Values ============
  /** Total value for primary data (average for derived metrics) */
  primaryTotal: number;
  /** Total value for comparison data (average for derived metrics) */
  comparisonTotal: number;
  /** Percent change between primary and comparison totals */
  percentChange: number | undefined;
  /** Last primary value */
  lastPrimaryValue: number | null;
  /** Last comparison value */
  lastComparisonValue: number | null;
  /** Percent change between last values */
  lastValuePercentChange: number | undefined;

  // ============ Formatted Values (display-ready strings) ============
  /** Formatted primary total */
  formattedPrimaryTotal: string;
  /** Formatted comparison total */
  formattedComparisonTotal: string;
  /** Formatted percent change */
  formattedPercentChange: string;
  /** Formatted last primary value */
  formattedLastPrimaryValue: string;
  /** Formatted last comparison value */
  formattedLastComparisonValue: string;
  /** Formatted percent change between last values */
  formattedLastValuePercentChange: string;

  // ============ Time Period Info ============
  /** Whether this is a YTD with Prior Year comparison */
  isYTDWithPriorYear: boolean;
  /** Whether this is Last Week with Prior Year comparison */
  isLastWeekWithPriorYear: boolean;
  /** The start weekId for the comparison period */
  comparisonStartWeekId: number;
  /** The end weekId for the comparison period */
  comparisonEndWeekId: number;
}

/**
 * A V2 hook for fetching derived metric data by weekId that returns
 * data in SplineChartV2 format.
 *
 * Derived metrics (like purchaseRate = unitsSold / traffic) require
 * fetching data from multiple indices and combining them.
 *
 * This hook:
 * - Uses useMetricByWeekIdV2 internally for proper YTD + Prior Year handling
 * - Properly fetches the full comparison year data
 * - Returns data in SplineChartV2 format without timestamp shifting
 */
export const useDerivedMetricByWeekIdV2 = ({
  indexName,
  fieldName,
  entityType,
  useMetricByWeekIdV2Args = {}
}: UseDerivedMetricByWeekIdV2Args): UseDerivedMetricByWeekIdV2Result => {
  const formatMetric = useMetricFormatter();

  // Get the derived field definition
  const field = useMemo(() => {
    return INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType);
  }, [indexName, fieldName, entityType]);

  // Parse the dependent fields from the aggregation function
  // Derived fields have 2 variables in format: {indexName}__{fieldName}
  const [[index1, field1], [index2, field2]] = useMemo(() => {
    const variables: string[] = field.aggregationFunction.variables();
    if (variables.length < 2) {
      console.warn(`Expected 2 variables for derived field ${fieldName}, got ${variables.length}`);
      return [['', ''], ['', '']];
    }
    return [
      variables[0].split(FIELD_PATH_SEPARATOR),
      variables[1].split(FIELD_PATH_SEPARATOR)
    ];
  }, [field.aggregationFunction, fieldName]);

  // Fetch data for dependent field 1 using V2 hook
  const metric1 = useMetricByWeekIdV2({
    indexName: index1,
    fieldName: field1,
    entityType,
    ...useMetricByWeekIdV2Args
  });

  // Fetch data for dependent field 2 using V2 hook
  const metric2 = useMetricByWeekIdV2({
    indexName: index2,
    fieldName: field2,
    entityType,
    ...useMetricByWeekIdV2Args
  });

  const isLoading = metric1.isLoading || metric2.isLoading;

  // Combine primary data from both metrics to calculate derived values
  const primaryData: SplineChartV2DataPoint[] = useMemo(() => {
    if (isLoading) return [];

    // Map weekId to values for both data sets
    const data1Map = new Map(metric1.primaryData.map((d) => [d.weekId, d.value]));
    const data2Map = new Map(metric2.primaryData.map((d) => [d.weekId, d.value]));

    // Get all unique weekIds
    const allWeekIds = Array.from(
      new Set([...metric1.primaryData.map((d) => d.weekId), ...metric2.primaryData.map((d) => d.weekId)])
    ).sort((a, b) => a - b);

    return allWeekIds.map((weekId) => {
      const value1 = data1Map.get(weekId);
      const value2 = data2Map.get(weekId);

      // If either value is null or undefined, return null for the derived value
      if (value1 === null || value1 === undefined || value2 === null || value2 === undefined) {
        return {
          weekId,
          value: null
        };
      }

      const derivedValue = field.aggregationFunction.evaluate({
        [`${index1}${FIELD_PATH_SEPARATOR}${field1}`]: value1,
        [`${index2}${FIELD_PATH_SEPARATOR}${field2}`]: value2
      });

      return {
        weekId,
        value: derivedValue
      };
    });
  }, [isLoading, metric1.primaryData, metric2.primaryData, field.aggregationFunction, index1, field1, index2, field2]);

  // Combine comparison data from both metrics
  const comparisonData: SplineChartV2DataPoint[] = useMemo(() => {
    if (isLoading) return [];

    // Map weekId to values for both data sets
    const data1Map = new Map(metric1.comparisonData.map((d) => [d.weekId, d.value]));
    const data2Map = new Map(metric2.comparisonData.map((d) => [d.weekId, d.value]));

    // Get all unique weekIds
    const allWeekIds = Array.from(
      new Set([...metric1.comparisonData.map((d) => d.weekId), ...metric2.comparisonData.map((d) => d.weekId)])
    ).sort((a, b) => a - b);

    return allWeekIds.map((weekId) => {
      const value1 = data1Map.get(weekId);
      const value2 = data2Map.get(weekId);

      // If either value is null or undefined, return null for the derived value
      if (value1 === null || value1 === undefined || value2 === null || value2 === undefined) {
        return {
          weekId,
          value: null
        };
      }

      const derivedValue = field.aggregationFunction.evaluate({
        [`${index1}${FIELD_PATH_SEPARATOR}${field1}`]: value1,
        [`${index2}${FIELD_PATH_SEPARATOR}${field2}`]: value2
      });

      return {
        weekId,
        value: derivedValue
      };
    });
  }, [isLoading, metric1.comparisonData, metric2.comparisonData, field.aggregationFunction, index1, field1, index2, field2]);

  // Build series for SplineChartV2
  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-primary`,
      name: field.displayName || fieldName,
      data: primaryData
    }),
    [indexName, fieldName, field.displayName, primaryData]
  );

  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-comparison`,
      name: `${field.displayName || fieldName} (Comparison)`,
      data: comparisonData
    }),
    [indexName, fieldName, field.displayName, comparisonData]
  );

  // Calculate totals (for derived metrics, we typically want averages, not sums)
  // Since conversion rate = unitsSold / traffic, we need the weighted average
  const primaryTotal = useMemo(() => {
    if (primaryData.length === 0) return 0;
    // For derived metrics like conversion rate, calculate from totals
    const total1 = metric1.primaryTotal;
    const total2 = metric2.primaryTotal;
    return field.aggregationFunction.evaluate({
      [`${index1}${FIELD_PATH_SEPARATOR}${field1}`]: total1,
      [`${index2}${FIELD_PATH_SEPARATOR}${field2}`]: total2
    });
  }, [primaryData.length, metric1.primaryTotal, metric2.primaryTotal, field.aggregationFunction, index1, field1, index2, field2]);

  const comparisonTotal = useMemo(() => {
    if (comparisonData.length === 0) return 0;
    const total1 = metric1.comparisonTotal;
    const total2 = metric2.comparisonTotal;
    return field.aggregationFunction.evaluate({
      [`${index1}${FIELD_PATH_SEPARATOR}${field1}`]: total1,
      [`${index2}${FIELD_PATH_SEPARATOR}${field2}`]: total2
    });
  }, [comparisonData.length, metric1.comparisonTotal, metric2.comparisonTotal, field.aggregationFunction, index1, field1, index2, field2]);

  const percentChange = useMemo(
    () => (comparisonTotal !== 0 ? calculatePercentChange(primaryTotal, comparisonTotal) : undefined),
    [primaryTotal, comparisonTotal]
  );

  // Last values
  const lastPrimaryValue = primaryData.length > 0 ? primaryData[primaryData.length - 1].value : null;
  const lastComparisonValue = comparisonData.length > 0 ? comparisonData[comparisonData.length - 1].value : null;
  const lastValuePercentChange =
    lastPrimaryValue !== null && lastComparisonValue !== null
      ? calculatePercentChange(lastPrimaryValue, lastComparisonValue)
      : undefined;

  const metricType = field.metricType;

  // ============ Formatted Values ============
  const formatOptions = {
    decimalPlaces: 2,
    showFullValue: metricType === METRICTYPE.PERCENT,
    showNegative: true
  };

  const formattedPrimaryTotal = useMemo(
    () => formatMetric(primaryTotal, metricType, formatOptions),
    [formatMetric, primaryTotal, metricType]
  );

  const formattedComparisonTotal = useMemo(
    () => formatMetric(comparisonTotal, metricType, formatOptions),
    [formatMetric, comparisonTotal, metricType]
  );

  const formattedPercentChange = useMemo(
    () =>
      percentChange !== undefined
        ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
        : '',
    [formatMetric, percentChange]
  );

  const formattedLastPrimaryValue = useMemo(
    () => (lastPrimaryValue !== null ? formatMetric(lastPrimaryValue, metricType, formatOptions) : ''),
    [formatMetric, lastPrimaryValue, metricType]
  );

  const formattedLastComparisonValue = useMemo(
    () => (lastComparisonValue !== null ? formatMetric(lastComparisonValue, metricType, formatOptions) : ''),
    [formatMetric, lastComparisonValue, metricType]
  );

  const formattedLastValuePercentChange = useMemo(
    () =>
      lastValuePercentChange !== undefined
        ? formatMetric(Math.abs(lastValuePercentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
        : '',
    [formatMetric, lastValuePercentChange]
  );

  return {
    // Series data
    primarySeries,
    comparisonSeries,

    // Raw data points
    primaryData,
    comparisonData,

    // Loading state
    isLoading,

    // Field info
    metricType,
    field,

    // Raw numeric values
    primaryTotal,
    comparisonTotal,
    percentChange,
    lastPrimaryValue,
    lastComparisonValue,
    lastValuePercentChange,

    // Formatted values
    formattedPrimaryTotal,
    formattedComparisonTotal,
    formattedPercentChange,
    formattedLastPrimaryValue,
    formattedLastComparisonValue,
    formattedLastValuePercentChange,

    // Time period info (from first metric, same for both)
    isYTDWithPriorYear: metric1.isYTDWithPriorYear,
    isLastWeekWithPriorYear: metric1.isLastWeekWithPriorYear,
    comparisonStartWeekId: metric1.comparisonStartWeekId,
    comparisonEndWeekId: metric1.comparisonEndWeekId
  };
};

export default useDerivedMetricByWeekIdV2;
